import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
const navLinks = [{
  title: 'HOME',
  href: '/'
}, {
  title: 'OUR SERVICES',
  href: '#',
  subLinks: [{
    title: 'BASIC HOME CLEANING',
    href: '/basic-home-cleaning'
  }, {
    title: 'COMMERCIAL CLEANING',
    href: '/commercial-cleaning'
  }, {
    title: 'SPRING / DEEP CLEANING',
    href: '/spring-deep-cleaning'
  }, {
    title: 'MOVE-IN / MOVE-OUT',
    href: '/move-in-move-out-quote'
  }, {
    title: 'AIRBNB / SHORT TERM RENTAL',
    href: '/airbnb-quote'
  }, {
    title: 'POST RENOVATION',
    href: '/post-renovation-quote'
  }]
}, {
  title: 'ABOUT US',
  href: '/about-us'
}, {
  title: 'CONTACT US',
  href: '/contact-us'
}, {
  title: 'FAQ',
  href: '/faq'
}];
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showBookOnline, setShowBookOnline] = useState(false);
  const bookOnlineRef = useRef<HTMLDivElement>(null);
  // Handle scroll for sticky nav background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Close Book Online dropdown on outside click
  useEffect(() => {
    if (!showBookOnline) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (bookOnlineRef.current && !bookOnlineRef.current.contains(e.target as Node)) {
        setShowBookOnline(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBookOnline]);
  // Animation variants
  const menuVariants = {
    closed: {
      opacity: 0,
      clipPath: 'circle(0% at calc(100% - 3rem) 3rem)',
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    open: {
      opacity: 1,
      clipPath: 'circle(150% at calc(100% - 3rem) 3rem)',
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const linkContainerVariants = {
    closed: {
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    },
    open: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };
  const linkVariants = {
    closed: {
      y: 50,
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    open: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <>
      {/* Sticky Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-6 md:px-12 py-4 transition-all duration-300 ${scrolled && !isOpen ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-transparent'}`} data-id="element-373">
        {/* Logo */}
        <Link to="/" className="block relative z-50" data-id="element-374">
          <img src="https://cdn.magicpatterns.com/uploads/5m425UHDJynsrvkfvBo6sp/sj-website-logo-colour.svg" alt="Smith & Jengs Cleaning Services" className={`w-20 h-20 md:w-24 md:h-24 object-contain transition-opacity duration-300 ${isOpen ? 'opacity-0' : 'opacity-100'}`} width={96} height={96} decoding="async" data-id="element-375" />
        </Link>

        {/* Right side: CTA + Hamburger */}
        <div className="flex items-center gap-4 relative z-50" data-id="element-376">
          {/* Book Online CTA with Dropdown */}
          <div ref={bookOnlineRef} className={`relative hidden md:block ${isOpen ? 'opacity-0 pointer-events-none' : ''}`} data-id="element-377">
            <button onClick={() => setShowBookOnline(prev => !prev)} className="flex items-center gap-2 px-5 py-2 rounded-full border border-[#e91e8c] text-[#e91e8c] bg-transparent text-xs font-semibold tracking-wider uppercase transition-all duration-300" data-id="element-378">
              Book Online
              <span className="inline-block transition-transform duration-300" style={{
              transform: showBookOnline ? 'rotate(90deg)' : 'rotate(0deg)'
            }} data-id="element-379">
                &rarr;
              </span>
            </button>

            <AnimatePresence data-id="element-380">
              {showBookOnline && <motion.div initial={{
              opacity: 0,
              y: -8,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -8,
              scale: 0.95
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="absolute right-0 top-full mt-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[260px] z-50" data-id="element-381">
                  {[{
                label: 'Basic Home Cleaning',
                to: '/basic-home-cleaning-quote'
              }, {
                label: 'Commercial Cleaning',
                to: '/commercial-cleaning-quote'
              }, {
                label: 'Spring / Deep Cleaning',
                to: '/spring-deep-cleaning-quote'
              }, {
                label: 'Move-In / Move-Out',
                to: '/move-in-move-out-quote'
              }, {
                label: 'Airbnb / Short Term Rental',
                to: '/airbnb-quote'
              }, {
                label: 'Post Renovation',
                to: '/post-renovation-quote'
              }].map(service => <Link key={service.to} to={service.to} onClick={() => setShowBookOnline(false)} className="flex items-center gap-3 px-5 py-3.5 text-[#1a2744] hover:bg-[#e91e8c]/10 hover:text-[#e91e8c] transition-colors text-sm font-medium tracking-wide border-b border-gray-100 last:border-b-0" data-id="element-382">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#e91e8c]" data-id="element-383" />
                      {service.label}
                    </Link>)}
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Hamburger / Close Button */}
          <button onClick={() => setIsOpen(!isOpen)} className={`relative w-14 h-14 rounded-full flex flex-col items-center justify-center gap-1.5 shadow-lg transition-colors focus:outline-none focus:ring-4 focus:ring-pink-300 ${isOpen ? 'bg-white hover:bg-gray-100' : 'bg-[#e91e8c] hover:bg-[#d0187c]'}`} aria-label={isOpen ? 'Close Menu' : 'Open Menu'} data-id="element-384">
            <motion.span animate={isOpen ? {
            rotate: 45,
            y: 8
          } : {
            rotate: 0,
            y: 0
          }} className={`w-6 h-0.5 rounded-full transition-colors ${isOpen ? 'bg-[#1a2744]' : 'bg-white'}`} data-id="element-385" />
            <motion.span animate={isOpen ? {
            opacity: 0
          } : {
            opacity: 1
          }} className={`w-6 h-0.5 rounded-full transition-colors ${isOpen ? 'bg-[#1a2744]' : 'bg-white'}`} data-id="element-386" />
            <motion.span animate={isOpen ? {
            rotate: -45,
            y: -8
          } : {
            rotate: 0,
            y: 0
          }} className={`w-6 h-0.5 rounded-full transition-colors ${isOpen ? 'bg-[#1a2744]' : 'bg-white'}`} data-id="element-387" />
          </button>
        </div>
      </header>

      {/* Full Screen Menu Overlay */}
      <AnimatePresence data-id="element-388">
        {isOpen && <motion.div initial="closed" animate="open" exit="closed" variants={menuVariants} className="fixed inset-0 z-40 bg-[#1a2744] flex flex-col justify-between px-6 md:px-12 pt-32 pb-12 overflow-y-auto" data-id="element-389">
            {/* White Logo in Menu */}
            <div className="absolute top-6 left-6 md:left-12" data-id="element-390">
              <img src="https://cdn.magicpatterns.com/uploads/1h9RWLFQ7TTAoyFwfGSDao/sj-website-logo-white.svg" alt="Smith & Jengs White Logo" className="w-20 h-20 md:w-24 md:h-24 object-contain" width={96} height={96} decoding="async" data-id="element-391" />
            </div>

            {/* Navigation Links */}
            <motion.nav variants={linkContainerVariants} className="flex flex-col items-center justify-center flex-1 mt-12" data-id="element-392">
              {navLinks.map((link, i) => <div key={i} className="overflow-hidden py-2 w-full flex flex-col items-center" data-id="element-393">
                  <motion.div variants={linkVariants} data-id="element-394">
                    {link.subLinks ? <div className="flex flex-col items-center w-full" data-id="element-395">
                        <button onClick={() => setActiveDropdown(activeDropdown === link.title ? null : link.title)} className="block text-5xl md:text-7xl lg:text-8xl font-bebas text-white hover:text-[#e91e8c] transition-colors duration-300 tracking-wide text-center" data-id="element-396">
                          {link.title}
                        </button>
                        <AnimatePresence data-id="element-397">
                          {activeDropdown === link.title && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="flex flex-col items-center gap-4 mt-6 overflow-hidden" data-id="element-398">
                              {link.subLinks.map((sub, j) => <Link key={j} to={sub.href} onClick={() => setIsOpen(false)} className="text-2xl md:text-4xl font-bebas text-[#5bbcd5] hover:text-[#e91e8c] transition-colors tracking-wide text-center" data-id="element-399">
                                  {sub.title}
                                </Link>)}
                            </motion.div>}
                        </AnimatePresence>
                      </div> : link.href.startsWith('/') && link.href !== '#' ? <Link to={link.href} onClick={() => setIsOpen(false)} className="block text-5xl md:text-7xl lg:text-8xl font-bebas text-white hover:text-[#e91e8c] transition-colors duration-300 tracking-wide text-center" data-id="element-400">
                        {link.title}
                      </Link> : <a href={link.href} onClick={() => setIsOpen(false)} className="block text-5xl md:text-7xl lg:text-8xl font-bebas text-white hover:text-[#e91e8c] transition-colors duration-300 tracking-wide text-center" data-id="element-401">
                        {link.title}
                      </a>}
                  </motion.div>
                </div>)}
            </motion.nav>

            {/* Bottom Contact Info */}
            <motion.div variants={{
          closed: {
            opacity: 0,
            y: 20
          },
          open: {
            opacity: 1,
            y: 0,
            transition: {
              delay: 0.8,
              duration: 0.6
            }
          }
        }} className="mt-12 flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12 text-white/70 text-sm md:text-base font-medium border-t border-white/10 pt-8" data-id="element-402">
              <a href="mailto:smithsandjengs@yahoo.com" className="hover:text-[#e91e8c] transition-colors" data-id="element-403">
                smithsandjengs@yahoo.com
              </a>
              <div className="flex gap-4" data-id="element-404">
                <a href="tel:07432285564" className="hover:text-[#e91e8c] transition-colors" data-id="element-405">
                  07432285564
                </a>
                <span className="text-white/30" data-id="element-406">|</span>
                <a href="tel:01869601439" className="hover:text-[#e91e8c] transition-colors" data-id="element-407">
                  01869601439
                </a>
              </div>
              <a href="https://www.facebook.com/SmithandJengs/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e91e8c] transition-colors" data-id="element-408">
                Facebook
              </a>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}